/**
 * Chat message parser utility
 * Handles parsing and formatting chat API responses
 */

/**
 * Extract transaction data from AI response
 */
export const extractTransactionData = (aiResponse) => {
  if (!aiResponse || aiResponse.type === 'conversational') {
    return null;
  }

  return {
    type: aiResponse.type,           // "sale" or "expense"
    amount: aiResponse.amount,        // numeric
    item: aiResponse.item,            // product name
    quantity: aiResponse.quantity,    // count
    category: aiResponse.category,    // AI-classified
  };
};

/**
 * Check if a message is a transaction (has ai_response with type/amount/item)
 */
export const isTransactionMessage = (message) => {
  return (
    message.ai_response &&
    message.ai_response.type &&
    (message.ai_response.type === 'sale' || message.ai_response.type === 'expense') &&
    message.ai_response.amount !== undefined &&
    message.ai_response.item
  );
};

/**
 * Check if a message is an error
 */
export const isErrorMessage = (message) => {
  return message.ai_response && message.ai_response.issue_type === 'validation_error';
};

/**
 * Check if a message is conversational
 */
export const isConversational = (message) => {
  return message.ai_response && message.ai_response.type === 'conversational';
};

/**
 * Format amount with ₦ symbol and commas
 */
export const formatAmount = (amount) => {
  if (!amount) return '₦0.00';
  return `₦${parseFloat(amount).toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Get display text for transaction type
 */
export const getTransactionTypeLabel = (type) => {
  return type === 'sale' ? 'Sale' : 'Expense';
};

/**
 * Parse markdown-like content and return JSX-ready components
 * Handles **bold**, 🎯 emojis, and line breaks
 */
export const parseMessageContent = (content) => {
  if (!content) return '';
  
  // Replace **text** with strong tags
  let parsed = content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  
  // ConvertNew lines to <br/>
  parsed = parsed.replace(/\n/g, '<br/>');
  
  return parsed;
};

/**
 * Get category badge color based on category name
 */
export const getCategoryColor = (category) => {
  const categoryColors = {
    'Office Supplies': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    'Utilities': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    'Equipment': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    'Marketing & Advertising': 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
    'Transportation': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    'Rent': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    'Beverages': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
    'Product Sales': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
    'Service Revenue': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  };

  return categoryColors[category] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
};
